/**
 * TurnstileWidget — Cloudflare Turnstile (CAPTCHA invisible)
 *
 * Utilisation : afficher le widget pour les utilisateurs anonymes avant
 * une action sensible (création de poll, premier message IA). Le token
 * généré doit être envoyé au serveur qui le vérifie via Cloudflare
 * siteverify (cf. `verifyTurnstileToken` côté Edge Function).
 *
 * Doctrine 11/05/2026 :
 * - Activé uniquement si `VITE_TURNSTILE_SITE_KEY` est défini en build.
 * - Si la clé est absente : composant ne rend rien (fail-open dev/local).
 * - Mode `managed` par défaut : Cloudflare décide quand afficher le challenge.
 * - Token consommable une seule fois côté serveur ; on refresh après utilisation.
 */

import {
  useEffect,
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { getEnv } from "../lib/env";
import { setTurnstileToken, clearTurnstileToken } from "../lib/turnstileToken";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: string | HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          "error-callback"?: () => void;
          "expired-callback"?: () => void;
          theme?: "auto" | "light" | "dark";
          size?: "normal" | "compact" | "invisible";
          appearance?: "always" | "execute" | "interaction-only";
          language?: string;
        },
      ) => string;
      reset: (widgetId?: string) => void;
      remove: (widgetId?: string) => void;
      getResponse: (widgetId?: string) => string | undefined;
      execute: (widgetId?: string) => void;
    };
    __turnstileLoaderPromise?: Promise<void>;
  }
}

const TURNSTILE_SCRIPT_SRC =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";

function loadTurnstileScript(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (window.__turnstileLoaderPromise) return window.__turnstileLoaderPromise;

  window.__turnstileLoaderPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector(
      `script[src^="${TURNSTILE_SCRIPT_SRC.split("?")[0]}"]`,
    );
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(), { once: true });
      return;
    }
    const script = document.createElement("script");
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Failed to load Turnstile script"));
    document.head.appendChild(script);
  });

  return window.__turnstileLoaderPromise;
}

export interface TurnstileWidgetHandle {
  /** Récupère le token actuel s'il existe. */
  getToken: () => string | null;
  /** Force le re-render du widget (token frais). */
  reset: () => void;
}

interface TurnstileWidgetProps {
  /** Callback appelé dès qu'un nouveau token est généré par Cloudflare. */
  onVerify?: (token: string) => void;
  /** Callback en cas d'erreur (réseau, challenge échoué…). */
  onError?: () => void;
  /** Callback quand le token expire (Cloudflare le révoque après ~5 min). */
  onExpire?: () => void;
  /** Mode d'affichage. `interaction-only` est le moins intrusif. */
  appearance?: "always" | "execute" | "interaction-only";
  /** Thème (auto par défaut). */
  theme?: "auto" | "light" | "dark";
  /** Taille du widget (`normal` pour la visibilité, `invisible` pour aucun rendu). */
  size?: "normal" | "compact" | "invisible";
}

/**
 * TurnstileWidget — composant React qui rend le widget Cloudflare Turnstile.
 *
 * Si `VITE_TURNSTILE_SITE_KEY` n'est pas défini, le composant ne rend rien.
 * Cela permet le dev local sans configuration.
 */
const TurnstileWidget = forwardRef<TurnstileWidgetHandle, TurnstileWidgetProps>(
  function TurnstileWidget(
    { onVerify, onError, onExpire, appearance, theme, size },
    ref,
  ) {
    const siteKey = getEnv("VITE_TURNSTILE_SITE_KEY");
    const { i18n } = useTranslation();
    // Turnstile accepte un code ISO 639-1 ("fr", "en", "es"). Sans ce param,
    // l'iframe Cloudflare s'affiche dans la locale par défaut du navigateur,
    // qui peut diverger de la langue de l'app (bug vu : UI EN, captcha FR).
    const language = (i18n.language || "fr").slice(0, 2);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const widgetIdRef = useRef<string | null>(null);
    const [scriptReady, setScriptReady] = useState(false);

    useImperativeHandle(
      ref,
      () => ({
        getToken: () => {
          if (!window.turnstile || !widgetIdRef.current) return null;
          return window.turnstile.getResponse(widgetIdRef.current) ?? null;
        },
        reset: () => {
          if (!window.turnstile || !widgetIdRef.current) return;
          window.turnstile.reset(widgetIdRef.current);
        },
      }),
      [],
    );

    useEffect(() => {
      if (!siteKey) return;
      let cancelled = false;
      loadTurnstileScript()
        .then(() => {
          if (!cancelled) setScriptReady(true);
        })
        .catch(() => {
          if (!cancelled) onError?.();
        });
      return () => {
        cancelled = true;
      };
    }, [siteKey, onError]);

    useEffect(() => {
      if (!scriptReady || !siteKey || !containerRef.current) return;
      if (!window.turnstile) return;
      if (widgetIdRef.current) return;

      try {
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: siteKey,
          callback: (token) => {
            setTurnstileToken(token);
            onVerify?.(token);
          },
          "error-callback": () => {
            clearTurnstileToken();
            onError?.();
          },
          "expired-callback": () => {
            clearTurnstileToken();
            onExpire?.();
          },
          appearance: appearance ?? "interaction-only",
          theme: theme ?? "auto",
          size: size ?? "normal",
          language,
        });
      } catch {
        onError?.();
      }

      const widgetId = widgetIdRef.current;
      return () => {
        if (window.turnstile && widgetId) {
          try {
            window.turnstile.remove(widgetId);
          } catch {
            /* widget déjà supprimé */
          }
        }
        widgetIdRef.current = null;
      };
    }, [
      scriptReady,
      siteKey,
      appearance,
      theme,
      size,
      language,
      onVerify,
      onError,
      onExpire,
    ]);

    if (!siteKey) return null;

    return <div ref={containerRef} data-testid="turnstile-widget" />;
  },
);

export default TurnstileWidget;
